import { generateTablePageButtons } from 'src/core/tables/generateTablePageButtons';
import { useGetDispatchOrderByWarehouse } from 'src/pages/warehouses/warehouseRequests';
import WarehouseSelector, { useActiveWarehouse, WarehouseSelectionRequired } from 'src/pages/warehouses/WarehouseSelector';
import { useCallback, useMemo, useState } from 'react';
import PageHeader from 'src/core/ui/PageHeader';
import Footer from 'src/core/ui/helpers/Footer';
import ContentCardsContainer, { CardType, ContentCard } from 'src/core/ui/ContentCard';
import { CustomDataGrid } from 'src/core/tables/CustomDataGrid';
import { useDispatchOrderRowsTable, useDispatchOrdersTable } from 'src/pages/warehouses/dispatch/useDispatchOrdersTable';
import DetailPanelContainer from 'src/core/tables/helpers/DetailPanelContainer';
import { Box, Typography } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import CustomButton from 'src/core/ui/CustomButton';
import OutboxIcon from '@mui/icons-material/Outbox';
import ArchiveTwoToneIcon from '@mui/icons-material/ArchiveTwoTone';
import DiscontinuedToggleButton from 'src/core/tables/helpers/DiscontinuedToggleButton';
import { useSettingsContext } from 'src/core/settings/useSettings';
import { getNestedValue } from 'src/core/settings/settings';
import { canUseDiscontinuedToggle } from 'src/shared/environment';
import { DispatchPickingListJobs, useDispatchOrderPrinting, useDispatchPrintQueue } from './useDispatchOrderPrinting';

export const DispatchOrdersTablePage = () => <WarehouseSelectionRequired><DispatchOrdersContent /></WarehouseSelectionRequired>;

const DispatchOrdersContent = () => {
    const { settings, setSetting } = useSettingsContext();
    const includeArchived = useMemo(() => getNestedValue(settings, ['IncludeDiscontinued', TABLE_NAME], false), [settings]);
    const { warehouseId, selectWarehouse } = useActiveWarehouse();
    const [refreshKey, setRefreshKey] = useState(0);
    const refresh = useCallback(() => setRefreshKey(current => current + 1), []);
    const printing = useDispatchOrderPrinting(warehouseId, refresh);
    const [columns, tableSettings, , activeRows] = useDispatchOrdersTable(warehouseId, refreshKey, includeArchived, { onPrint: printing.startPrint, disabled: printing.printing || printing.autoPrintPendingOrderId != null });
    const queue = useDispatchPrintQueue({ warehouseId, tableSettings, activeRows, includeArchived, printing, onRefresh: refresh });
    return (
        <>
            <DispatchPickingListJobs printing={printing} />
            <PageHeader {...{
                title: 'Dispatch Orders', subTitle: 'View and print picking lists for dispatch orders',
                components: [
                    { component: <WarehouseSelector value={warehouseId} onChange={selectWarehouse} /> },
                    ...(canUseDiscontinuedToggle ? [{ component: <DiscontinuedToggleButton settings={settings} setSetting={setSetting} tableName={TABLE_NAME} icon={<ArchiveTwoToneIcon />} tooltipOn="Hide archived" tooltipOff="Show archived" /> }] : []),
                    { component: <CustomButton title="Dispatch Station" icon={<OutboxIcon />} actionUrl="/warehouse/dispatch-orders/create" /> },
                    { component: <CustomButton title={`Print collective list (${queue.selectedPickingListCount})`} icon={<PrintIcon />} disabled={printing.printing || warehouseId == null || queue.selectedPickingListCount === 0} onClick={queue.startCollectivePrint} /> },
                    { isInGroupedMenu: true, component: <CustomButton title="Printing Settings" icon={<PrintIcon />} actionUrl="/settings/webclient" /> },
                    { position: 'center', component: <AutoPrintToggle enabled={printing.autoPrintEnabled} onChange={printing.setAutoPrintEnabled} status={queue.autoPrintStatus} /> },
                    ...generateTablePageButtons(tableSettings),
                ],
            }} />
            <ContentCardsContainer>
                <ContentCard cardId="DispatchOrdersPrinter" cardTypes={[CardType.noHeader]} contentPadding={0}>
                    <CustomDataGrid key={`${tableSettings.tableName}-${includeArchived ? 'with-archived' : 'active-only'}`} columns={columns} tableSettings={tableSettings} getDetailPanelContent={(row) => <DispatchOrderDetailPanel row={row} />} />
                </ContentCard>
            </ContentCardsContainer>
            <Footer />
        </>
    );
};

const TABLE_NAME = "DispatchOrdersTable";

const emptyRows: any[] = [];

export const DispatchOrderDetailPanel = ({ row }) => {
    const { warehouseId } = useActiveWarehouse();
    const { rows: order, status }: any = useGetDispatchOrderByWarehouse(row?.row?.DispatchOrderId, warehouseId);
    const [columns, tableSettings] = useDispatchOrderRowsTable(order?.DispatchOrderRows ?? emptyRows, status);

    return (
        <DetailPanelContainer>
            <CustomDataGrid columns={columns} tableSettings={tableSettings} />
        </DetailPanelContainer>
    );
};

const AutoPrintToggle = ({ enabled, onChange, status }) => (
    <Box sx={{ minWidth: 160 }}>
        <CustomButton title={enabled ? 'Printing Enabled' : 'Printing Disabled'} color={enabled ? 'success' : 'error'} tooltipText={enabled ? 'Disable automatic picking list printing' : 'Enable automatic picking list printing'} onClick={() => onChange(!enabled)} />
        <Typography variant="caption" display="block" aria-live="polite">{status}</Typography>
    </Box>
);

export default DispatchOrdersTablePage;

export { default as WarehouseFilter } from 'src/pages/warehouses/WarehouseSelector';
